import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  IBillingCycle,
  IProduct,
  ProductsSubprojectService,
} from 'src/app/services/products-subproject/products-subproject.service';
import { jsonValidator } from 'src/app/validators/json-validator';

@Component({
  selector: 'app-stripe-product-form',
  templateUrl: './stripe-product-form.component.html',
  styleUrls: ['./stripe-product-form.component.scss'],
})
export class StripeProductFormComponent {
  @Input() billingCycles: IBillingCycle[] = [];
  @Input() set stripeProductId(value: string | undefined) {
    if (value) {
      this.loadProductDetails(value);
    }
  }
  @Output() stripeProductSaved = new EventEmitter<void>();
  @Output() noStripeProduct = new EventEmitter<void>();
  @Output() productIdChanged = new EventEmitter<string>();
  public productDetailsForm!: FormGroup;
  private subprojectProduct!: IProduct;

  get billingCycleControl() {
    return this.productDetailsForm.get('billingCycleId') as FormControl;
  }

  get billingDayControl() {
    return this.productDetailsForm.get('billingDay') as FormControl;
  }

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly productsSubprojectService: ProductsSubprojectService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.initializeProductDetailsForm();
  }

  private initializeProductDetailsForm(): void {
    this.productDetailsForm = this.formBuilder.group({
      stripeProductId: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      metadata: ['', [jsonValidator]],
      billingCycleId: ['', Validators.required],
      billingDay: [null, [Validators.min(1), Validators.max(31)]],
      active: [true],
      price: [0, [Validators.required, Validators.min(0)]],
    });
  }

  private async loadProductDetails(stripeProductId: string) {
    const subprojectProductResponse =
      await this.productsSubprojectService.getProductByStripeProductId(stripeProductId);
    if (subprojectProductResponse.success) {
      if (!subprojectProductResponse.data.length) {
        this.noStripeProduct.emit();
        this.productDetailsForm.disable();
        return;
      }
      this.subprojectProduct = subprojectProductResponse.data[0];
      this.populateProductDetailsForm();
      this.emitProductId();
    } else {
      this.snackBar.open('Error loading product details', 'Error', {
        duration: 2000,
      });
    }
  }

  private populateProductDetailsForm(): void {
    if (this.subprojectProduct) {
      this.productDetailsForm.enable();
      this.productDetailsForm.patchValue({
        stripeProductId: this.subprojectProduct.stripeProductId || '',
        name: this.subprojectProduct.name || '',
        description: this.subprojectProduct.description || '',
        metadata: JSON.stringify(this.subprojectProduct.metadata || {}, null, 2),
        billingCycleId: this.subprojectProduct.billingCycleId || '',
        billingDay: this.subprojectProduct.billingDay || null,
        active: this.subprojectProduct.active ?? true,
        // images: '', // Images not available in IProduct interface
        price: (this.subprojectProduct as any).price || 0,
      });
    }
  }

  public formatProductJsonOnBlur(controlName: string): void {
    const ctrl = this.productDetailsForm.get(controlName);
    if (!ctrl) return;

    const value = ctrl.value;

    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);

      if (formatted !== value) {
        ctrl.setValue(formatted, { emitEvent: false });
      }
    } catch {
      // Do nothing — validation already handles invalid JSON
    }
  }

  public async saveProductDetails(): Promise<void> {
    if (this.productDetailsForm.valid) {
      const formValue = this.productDetailsForm.value;

      try {
        // Parse JSON and images
        const productData = {
          ...formValue,
          metadata: JSON.parse(formValue.metadata || '{}'),
          // images: formValue.images
          //   ? formValue.images.split('\n').filter((url: string) => url.trim())
          //   : [],
        };
        const response = await this.productsSubprojectService.updateProduct(
          this.subprojectProduct.id,
          productData,
        );
        if (!response.success) {
          this.snackBar.open(
            response.message || response.error || 'Error saving product details',
            'Error',
            {
              duration: 3000,
            },
          );
          return;
        }
        this.stripeProductSaved.emit();
      } catch (error) {
        this.snackBar.open('Error saving product details', 'Error', {
          duration: 3000,
        });
      }
    }
  }

  public resetProductDetailsForm(): void {
    this.initializeProductDetailsForm();
  }

  private emitProductId(): void {
    if (this.subprojectProduct?.id) this.productIdChanged.emit(this.subprojectProduct.id);
  }
}
